import type { ComponentChildren, RefObject, JSX } from 'preact';
import { cn } from 'utils/cn';
import mergeRefs from 'merge-refs';
import { useRef } from 'preact/hooks';

interface Props {
	className?: string;
	inputRef?: RefObject<HTMLInputElement>;
	beforeChildren?: ComponentChildren;
	children?: ComponentChildren;
	color?: 'red' | 'white';
	inputProps?: JSX.IntrinsicElements['input'];
	title?: string;
	name?: string;
	isToggle?: boolean;
	hasError?: boolean;
}

export function Checkbox({
	beforeChildren,
	color = 'red',
	className,
	children,
	inputRef,
	inputProps,
	title,
	name,
	isToggle = false,
	hasError,
	...restProps
}: Props & JSX.IntrinsicElements['input']) {
	const ref = useRef<HTMLInputElement>(null);

	return (
		<label
			className={cn(
				'inline-flex items-center relative cursor-pointer text-sm font-medium leading-[1]',
				className,
			)}
			title={title}
		>
			{beforeChildren}
			{isToggle ? (
				<input
					data-test={
						name
							? `input-${name}`
							: typeof inputProps?.name === 'string'
								? `input-${inputProps.name}`
								: undefined
					}
					type="checkbox"
					ref={mergeRefs(inputRef, ref)}
					class={'flex-none toggle shrink-0'}
					{...restProps}
					{...inputProps}
				/>
			) : (
				<span class="relative w-[18px] h-[18px] shrink-0">
					<input
						data-test={
							name
								? `input-${name}`
								: typeof inputProps?.name === 'string'
									? `input-${inputProps.name}`
									: undefined
						}
						type="checkbox"
						ref={mergeRefs(inputRef, ref)}
						class="flex-none peer opacity-0 absolute"
						{...restProps}
						{...inputProps}
					/>
					<span
						class={cn(
							'inline-block w-full h-full border-2 border-solid rounded-sm',
							color === 'red' && 'border-dark-blue-25',
							color === 'white' && 'border-white-50 ',
							hasError && 'border-red',
						)}
					/>

					<svg
						class={cn(
							'w-full h-full peer-checked:opacity-100 opacity-0 absolute top-0 left-0',
							color == 'white' && 'text-white',
							(color == 'red' || hasError) && 'text-red',
						)}
						width="18"
						height="18"
						viewBox="0 0 18 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M2 18C1.45 18 0.979167 17.8042 0.5875 17.4125C0.195833 17.0208 0 16.55 0 16V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H16C16.55 0 17.0208 0.195833 17.4125 0.5875C17.8042 0.979167 18 1.45 18 2V16C18 16.55 17.8042 17.0208 17.4125 17.4125C17.0208 17.8042 16.55 18 16 18H2ZM7.6 12.775C7.73333 12.775 7.85833 12.7542 7.975 12.7125C8.09167 12.6708 8.2 12.6 8.3 12.5L13.975 6.825C14.1583 6.64167 14.25 6.41667 14.25 6.15C14.25 5.88333 14.15 5.65 13.95 5.45C13.7667 5.26667 13.5333 5.175 13.25 5.175C12.9667 5.175 12.7333 5.26667 12.55 5.45L7.6 10.4L5.425 8.225C5.24167 8.04167 5.01667 7.95 4.75 7.95C4.48333 7.95 4.25 8.05 4.05 8.25C3.86667 8.43333 3.775 8.66667 3.775 8.95C3.775 9.23333 3.86667 9.46667 4.05 9.65L6.9 12.5C7 12.6 7.10833 12.6708 7.225 12.7125C7.34167 12.7542 7.46667 12.775 7.6 12.775Z"
							fill="currentColor"
						/>
					</svg>
				</span>
			)}{' '}
			<span class="leading-[17px] [&>*]:leading-[17px]">{children}</span>
		</label>
	);
}
